import { SELECTED_RING_LOCAL_STORAGE_KEY } from '../FeatureFlagLocalStorageConstants';
import {
    NinjaFlight,
    NinjaFlightsResponse,
    FlightConfig,
} from '../store/schema/NinjaFlightsResponse';
import { getStore } from '../store/store';
import { formatDate, now, subDays, getDay } from 'owa-datetime';
import { updateActiveFlights, updateLocalStorageOverrides } from 'owa-feature-flags';
import { resetFeatureFlags } from 'owa-feature-flags';
import featureDefinitions from 'owa-feature-flags/lib/store/featureDefinitions';
import { getAccessTokenforResource } from 'owa-tokenprovider';
import { orchestrator } from 'satcheljs';
import onFlightsApiResponseProcessed from '../actions/onFlightsApiResponseProcessed';
import onFlightConfigSelected from '../actions/onFlightConfigSelected';
import { setItem } from 'owa-local-storage';

/**
 * When flight config option changes...
 * - Ensure there's flight data in the store (make API request if not present)
 * - Apply flights enabled in that flight config
 */
orchestrator(onFlightConfigSelected, async actionMessage => {
    await getFlightData();
    applyFlightConfig(actionMessage.flightConfig);
});

/**
 * This function ensures flight data is present in the store (making an API
 * request to Ninja if there's no data in the store).
 */
const getFlightData = () => {
    // If the store already has flight info, no need to make another request.
    if (getStore().microsoftFlights.length > 0) {
        return new Promise(resolve => {
            resolve();
        });
    }

    return getAccessTokenforResource(window.location.origin).then(token => {
        return fetch(
            `https://ninja.outlookweb.io/api/flights?date=${getFormattedTargetDate()}&token=${token}`
        )
            .then(rawResponse => {
                return rawResponse.json();
            })
            .then(ninjaResponse => {
                processNinjaFlightsResponse(ninjaResponse);
            })
            .catch(() => {
                // Fail silently. 🤐
            });
    });
};

/**
 * Helper function that processes the Ninja API response into the format expected
 * by the store.
 */
const processNinjaFlightsResponse = (response: NinjaFlightsResponse): void => {
    const dogfoodFlights = [];
    const microsoftFlights = [];
    const wwFlights = [];

    const flights: NinjaFlight[] = response.list;
    for (const flight of flights) {
        for (const ring of flight.ringNames) {
            const normalizedFlightName = flight.feature.toUpperCase();

            switch (ring) {
                case 'DOGFOOD':
                    dogfoodFlights.push(normalizedFlightName);
                    break;
                case 'MICROSOFT':
                    microsoftFlights.push(normalizedFlightName);
                    break;
                case 'WW':
                    wwFlights.push(normalizedFlightName);
                    break;
            }
        }
    }

    onFlightsApiResponseProcessed(dogfoodFlights, microsoftFlights, wwFlights);
};

/**
 * Helper function to apply the flight config selected by the user.
 */
const applyFlightConfig = (selectedFlightConfig: FlightConfig) => {
    // Set selected flight config in local storage (for next page load).
    setItem(window, SELECTED_RING_LOCAL_STORAGE_KEY, selectedFlightConfig);

    // If user selects the default flights option, then just reset flights.
    if (selectedFlightConfig === 'DEFAULT') {
        resetFeatureFlags();
        return;
    }

    const allFlights = [...Object.keys(featureDefinitions)].map(flight => flight.toUpperCase());
    const featureOverrides = [];

    let flightsToEnable = [];
    switch (selectedFlightConfig) {
        case 'DOGFOOD':
            flightsToEnable = getStore().dogfoodFlights;
            break;
        case 'MICROSOFT':
            flightsToEnable = getStore().microsoftFlights;
            break;
        case 'WW':
            flightsToEnable = getStore().wwFlights;
            break;
    }

    for (const flight of allFlights) {
        // Normalize flight name.
        const normalizedFlightName = flight.toUpperCase();

        // Always enabled fwk-devTools flight.
        if (normalizedFlightName === 'FWK-DEVTOOLS') {
            featureOverrides.push({
                name: normalizedFlightName,
                isEnabled: true,
            });
            continue;
        }

        // Determine if flight should be enabled.
        const shouldEnableFlight = flightsToEnable.indexOf(normalizedFlightName) > -1;

        featureOverrides.push({
            name: normalizedFlightName,
            isEnabled: shouldEnableFlight,
        });
    }

    updateActiveFlights(featureOverrides);
    updateLocalStorageOverrides(featureOverrides);
};

/**
 * Helper function to get correctly formatted data param for Ninja API that
 * returns flight data.
 */
const getFormattedTargetDate = (): string => {
    // Grab day from 2 days ago (should have data).
    let targetDate = subDays(now(), 2);

    // If target date is a weekend, subtract 2 days to guarantee a week day.
    const isTargetDateWeekend = getDay(targetDate) === 0 || getDay(targetDate) === 6;
    targetDate = isTargetDateWeekend ? subDays(now(), 2) : targetDate;

    // Format target date in form that API expects.
    return formatDate(targetDate, 'yyyy-MM-dd');
};
